import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { campaignApi } from "@/lib/api";
import { computeFileSha256 } from "@/lib/fileChecksum";
import type { FCAData } from "@/types/fca";
import { estadoFitoConfig, riscoConfig, statusRevisaoConfig } from "@/config/cta-config";
import FcaHeaderQr from "./FcaHeaderQr";
import TreeMoreActions, { type MoreAction } from "./TreeMoreActions";
import TreeBasePhoto from "@/components/shared/TreeBasePhoto";

interface Props {
  data: FCAData;
  onEdit: () => void;
  /** Abre a aba Fotos, onde se adicionam fotos alem da principal. */
  onAddPhotos: () => void;
  /** Itens do menu "Mais ações". */
  moreActions: MoreAction[];
}

// Mesmo limite do servidor (POST /trees/{id}/photo), avisado antes do envio.
const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

export default function TreeProfileHeader({ data, onEdit, onAddPhotos, moreActions }: Props) {
  const navigate = useNavigate();
  const photoInput = useRef<HTMLInputElement | null>(null);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function handlePhoto(file: File | null) {
    if (!file) return;
    setPhotoMsg(null);
    if (!file.type.startsWith("image/")) {
      setPhotoMsg({ ok: false, text: "Selecione um arquivo de imagem." });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoMsg({ ok: false, text: "Imagem acima do limite de 15 MB." });
      return;
    }
    if (hasPhoto && !window.confirm("Substituir a foto principal do exemplar? A troca fica registrada no histórico.")) return;
    setPhotoBusy(true);
    try {
      // Hash calculado no navegador: o servidor recusa se o arquivo mudar no caminho.
      const checksum = await computeFileSha256(file);
      await campaignApi.uploadTreePhoto(Number(data.id), file, checksum ?? undefined);
      setPhotoVersion((v) => v + 1);
      setPhotoMsg({ ok: true, text: "Foto registrada." });
    } catch (e) {
      setPhotoMsg({ ok: false, text: e instanceof Error ? e.message : "Não foi possível enviar a foto." });
    } finally {
      setPhotoBusy(false);
      if (photoInput.current) photoInput.current.value = "";
    }
  }

  const treeId = Number(data.id);
  const hasCoords = data.latitude !== "" && data.latitude !== "—" && data.longitude !== "" && data.longitude !== "—";
  const estadoFito = estadoFitoConfig[data.estadoFitossanitario] || { badge: "bg-background-100 text-foreground-500", label: data.estadoFitossanitario };
  const risco = riscoConfig[data.classeRisco] || { badge: "bg-background-100 text-foreground-500", label: data.classeRisco };
  const statusRev = statusRevisaoConfig[data.statusRevisao] || { badge: "bg-background-100 text-foreground-500", label: data.statusRevisao };

  return (
    <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
      {/* Top section with photo + info */}
      <div className="flex flex-col md:flex-row">
        {/* Photo: a imagem fica livre; o botao de foto fica na faixa de baixo */}
        <div className="md:w-[260px] lg:w-[320px] shrink-0 flex flex-col border-b md:border-b-0 md:border-r border-background-200/70">
          <div className="relative h-[300px] sm:h-[340px] md:h-auto md:flex-1 md:min-h-[320px] bg-background-100">
            <TreeBasePhoto
              treeId={Number(data.id) || null}
              alt={`${data.nomePopular} - ${data.codigoCta}`}
              // Foto inteira (contain): o recorte cortava a base do exemplar em fotos verticais.
              className="absolute inset-0 w-full h-full object-contain"
              version={photoVersion}
              onStatus={setHasPhoto}
            />
            <div className="absolute top-3 left-3 right-3 flex flex-wrap gap-1.5">
              {!data.estadoFitossanitario && !data.classeRisco ? (
                <span className="badge bg-white/90 text-foreground-600">
                  <i className="ri-information-line mr-1 text-[10px]"></i>
                  Sem avaliação validada
                </span>
              ) : (
                <>
                  {data.estadoFitossanitario && (
                    <span className={`badge ${estadoFito.badge}`}>
                      <i className="ri-heart-pulse-line mr-1 text-[10px]"></i>
                      {estadoFito.label}
                    </span>
                  )}
                  {data.classeRisco && (
                    <span className={`badge ${risco.badge}`}>
                      <i className="ri-alert-line mr-1 text-[10px]"></i>
                      Risco {risco.label}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="px-3 py-2.5 border-t border-background-200/70 bg-background-50 flex flex-wrap items-center justify-center gap-2 text-center">
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handlePhoto(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              onClick={() => photoInput.current?.click()}
              disabled={photoBusy}
              className="btn-outline text-xs py-1.5 bg-white disabled:opacity-60"
            >
              <i className={photoBusy ? "ri-loader-4-line animate-spin" : "ri-camera-line"} aria-hidden="true"></i>
              {photoBusy ? "Enviando..." : hasPhoto ? "Trocar foto principal" : "Registrar foto principal"}
            </button>
            {photoMsg && (
              <span role="status" className={`w-full text-[11px] ${photoMsg.ok ? "text-green-700" : "text-red-700"}`}>
                {photoMsg.text}
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 p-4 sm:p-5 flex flex-col justify-between">
          <div>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-3 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-accent-500 transition-colors">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[10px]" aria-hidden="true"></i>
              <Link to="/cta" className="hover:text-accent-500 transition-colors">CTA Arbóreo</Link>
              <i className="ri-arrow-right-s-line text-[10px]" aria-hidden="true"></i>
              <span className="text-foreground-700 font-medium">FCA</span>
              <i className="ri-arrow-right-s-line text-[10px]" aria-hidden="true"></i>
              <span className="text-accent-600 font-medium truncate">{data.codigoCta}</span>
            </nav>

            {/* Code + name */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-foreground-500 bg-background-100 px-2 py-0.5 rounded-md">
                    {data.codigoCta}
                  </span>
                  <span className={`badge ${statusRev.badge}`}>{statusRev.label}</span>
                  <span className="badge bg-secondary-100 text-secondary-700">{data.tipo}</span>
                </div>
                <h1 className="text-xl font-bold text-foreground-900 font-heading leading-tight">
                  {data.nomePopular}
                </h1>
                <p className="text-sm text-foreground-500 italic mt-0.5">
                  {data.nomeCientifico}
                </p>
              </div>
              <FcaHeaderQr treeId={Number(data.id)} />
            </div>
          </div>

          {/* Acoes: no celular, grade de 2 colunas com botoes da largura total; a partir de sm, em linha */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 mt-3 pt-3 border-t border-background-200/70">
            <button
              type="button"
              onClick={() => navigate(`/cta/${treeId}/inspecoes/nova`)}
              className="btn-primary text-xs col-span-2 sm:col-span-1"
            >
              <i className="ri-clipboard-line" aria-hidden="true"></i>
              Nova vistoria
            </button>
            <button type="button" onClick={onAddPhotos} className="btn-outline text-xs">
              <i className="ri-image-add-line" aria-hidden="true"></i>
              Adicionar fotos
            </button>
            <button type="button" onClick={onEdit} className="btn-outline text-xs">
              <i className="ri-edit-line" aria-hidden="true"></i>
              Editar cadastro
            </button>
            <button
              type="button"
              onClick={() => navigate(`/mapa?arvore=${treeId}`)}
              disabled={!hasCoords}
              title={hasCoords ? undefined : "Exemplar sem coordenada cadastrada"}
              className="btn-outline text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-map-pin-line" aria-hidden="true"></i>
              Abrir no mapa
            </button>
            <button
              type="button"
              // Mesma aba: a sessao fica no sessionStorage, que uma aba nova nao herda.
              onClick={() => navigate(`/cta/${treeId}/pdf`)}
              className="btn-outline text-xs"
            >
              <i className="ri-file-pdf-line" aria-hidden="true"></i>
              Gerar PDF
            </button>
            {moreActions.length > 0 && (
              <div className="col-span-2 sm:col-span-1">
                <TreeMoreActions actions={moreActions} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}